import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton, Amount,
} from '@redpanda/ui';
import { formatTableDate, useNumberInput } from '@redpanda/utils';
import { getSalarySettings, createSalarySetting, deleteSalarySetting } from '../services/api';
import { Employee } from '../stores/employeeStore';
import { apiErrorMessage } from '../utils/apiError';
import { ymd } from '../utils/attendance';

interface SalarySetting {
  id: string;
  baseSalary: number;
  allowances: Array<{ name: string; amount: number }> | Record<string, number> | null;
  pensionSelfRate: number;
  effectiveDate: string;
}

interface FormData {
  effectiveDate: string;
  baseSalary: string;
  pensionSelfRate: string;
  allowances: Array<{ name: string; amount: string }>;
}

/** 津貼明細：新資料是 [{ name, amount }]，舊資料是 { 名稱: 金額 } */
const allowanceList = (value: SalarySetting['allowances']) =>
  Array.isArray(value) ? value : Object.entries(value || {}).map(([name, amount]) => ({ name, amount: Number(amount) || 0 }));

/**
 * 員工的薪資設定（一般企業做法：底薪＋固定津貼＋勞退自提；調薪時新增一筆，依生效日套用，舊的留作歷史）
 * 每月「產生薪資」與新增薪資時依這裡的設定帶入；只有管理員看得到
 * 新增要等伺服器回應（§3-17 等待型）
 */
const SalarySettingDialog: React.FC<{ employee: Employee; onClose: () => void }> = ({ employee, onClose }) => {
  const { t, i18n } = useTranslation(['pages', 'translation']);
  const [settings, setSettings] = useState<SalarySetting[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<SalarySetting | null>(null);
  const amountInput = useNumberInput('decimal');
  const { register, control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: { effectiveDate: ymd(new Date()), baseSalary: '', pensionSelfRate: '0', allowances: [] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'allowances' });

  const load = async () => {
    try {
      const { data } = await getSalarySettings(employee.id);
      setSettings(data);
      return data as SalarySetting[];
    } catch (error) {
      toast.error(apiErrorMessage(error, 'translation:common.messages.fetchFailed'));
      setSettings([]);
      return [];
    }
  };

  // 打開時載入歷史，表單先帶入目前的設定（調薪時只要改金額與生效日）
  useEffect(() => {
    load().then(list => {
      const latest = list[0];
      if (latest) {
        reset({
          effectiveDate: ymd(new Date()),
          baseSalary: String(latest.baseSalary),
          pensionSelfRate: String(latest.pensionSelfRate ?? 0),
          allowances: allowanceList(latest.allowances).map(a => ({ name: a.name, amount: String(a.amount) })),
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      await createSalarySetting({
        employeeId: employee.id,
        effectiveDate: `${data.effectiveDate}T00:00:00.000Z`,
        baseSalary: Number(data.baseSalary),
        pensionSelfRate: Number(data.pensionSelfRate),
        allowances: data.allowances.filter(a => a.name.trim()).map(a => ({ name: a.name.trim(), amount: Number(a.amount) || 0 })),
      });
      toast.success(t('pages:salarySetting.saveSuccess'));
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'translation:common.messages.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteSalarySetting(deleting.id);
      setDeleting(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'translation:common.messages.deleteFailed'));
    }
  };

  const allowancesTotal = watch('allowances').reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const today = ymd(new Date());
  // 目前適用的那一筆：生效日在今天以前的最新一筆
  const currentId = settings?.find(s => s.effectiveDate.slice(0, 10) <= today)?.id;

  return (
    <>
      <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-2xl">
        <DialogHeader
          title={t('pages:salarySetting.title', { name: employee.name })}
          onClose={onClose}
          closeDisabled={saving}
          closeLabel={t('translation:common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-6">
            {/* 歷史：一個容器＋細線分隔 */}
            <section>
              <h3 className="text-base font-semibold text-fg mb-2">{t('pages:salarySetting.history')}</h3>
              {settings === null ? (
                <p className="text-sm text-fg-secondary">{t('translation:common.status.loading')}</p>
              ) : settings.length === 0 ? (
                <p className="text-sm text-fg-secondary">{t('pages:salarySetting.empty')}</p>
              ) : (
                <ul className="divide-y divide-line rounded-md border border-line text-sm">
                  {settings.map(setting => {
                    const allowances = allowanceList(setting.allowances);
                    return (
                      <li key={setting.id} className="flex items-baseline gap-4 px-4 py-2.5">
                        <span className="w-24 tabular-nums text-fg-secondary">{formatTableDate(setting.effectiveDate, i18n.language)}</span>
                        <span className="flex-1 min-w-0 text-fg">
                          <span className="tabular-nums"><Amount value={setting.baseSalary} /></span>
                          {allowances.length > 0 && (
                            <span className="text-fg-secondary">
                              {' ＋ '}{allowances.map(a => `${a.name} $${a.amount.toLocaleString()}`).join('、')}
                            </span>
                          )}
                          {setting.pensionSelfRate > 0 && (
                            <span className="text-fg-secondary">{'・'}{t('pages:salarySetting.selfRateValue', { rate: setting.pensionSelfRate })}</span>
                          )}
                        </span>
                        {setting.id === currentId && <span className="text-[13px] text-accent">{t('pages:salarySetting.current')}</span>}
                        <button type="button" onClick={() => setDeleting(setting)} className="text-sm text-red-600 hover:text-red-700 dark:text-red-400">
                          {t('translation:common.actions.delete')}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section>
              <h3 className="text-base font-semibold text-fg mb-1">{t('pages:salarySetting.newTitle')}</h3>
              <p className="text-[13px] text-fg-secondary mb-4">{t('pages:salarySetting.newHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-5">
                <FormFieldWithError label={t('pages:salarySetting.effectiveDate')} required error={errors.effectiveDate?.message} htmlFor="effectiveDate">
                  <Input id="effectiveDate" type="date" {...register('effectiveDate', { required: t('translation:common.forms.validation.required') })} variant={errors.effectiveDate ? 'error' : 'default'} />
                </FormFieldWithError>
                <FormFieldWithError label={t('pages:payroll.form.baseSalary')} required error={errors.baseSalary?.message} htmlFor="baseSalary">
                  <Input
                    id="baseSalary"
                    inputMode="decimal"
                    className="text-right tabular-nums"
                    {...amountInput.bind(register('baseSalary', { required: t('pages:payroll.form.enterBaseSalary') }))}
                    variant={errors.baseSalary ? 'error' : 'default'}
                  />
                </FormFieldWithError>
                <FormFieldWithError label={t('pages:salarySetting.pensionSelfRate')} helpText={t('pages:salarySetting.pensionSelfRateHint')} htmlFor="pensionSelfRate">
                  <CustomSelect
                    id="pensionSelfRate"
                    value={watch('pensionSelfRate')}
                    onChange={value => setValue('pensionSelfRate', value)}
                    options={[0, 1, 2, 3, 4, 5, 6].map(rate => ({ value: String(rate), label: rate === 0 ? t('pages:salarySetting.noSelfContribution') : `${rate}%` }))}
                  />
                </FormFieldWithError>
              </div>

              {/* 固定津貼：每列名稱＋金額 */}
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-sm font-medium text-fg">{t('pages:salarySetting.allowances')}</span>
                  {allowancesTotal > 0 && <span className="text-[13px] text-fg-secondary tabular-nums">{t('pages:salarySetting.allowancesTotal')} <Amount value={allowancesTotal} /></span>}
                </div>
                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <Input {...register(`allowances.${index}.name` as const)} placeholder={t('pages:salarySetting.allowanceName')} aria-label={t('pages:salarySetting.allowanceName')} className="flex-1" />
                      <Input
                        inputMode="decimal"
                        className="w-36 text-right tabular-nums"
                        aria-label={t('pages:salarySetting.allowanceAmount')}
                        placeholder="0"
                        {...amountInput.bind(register(`allowances.${index}.amount` as const))}
                      />
                      <button type="button" onClick={() => remove(index)} className="p-2 text-fg-secondary hover:text-fg" aria-label={t('translation:common.actions.delete')}>
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => append({ name: '', amount: '' })} className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
                    <PlusIcon className="h-4 w-4" />{t('pages:salarySetting.addAllowance')}
                  </button>
                </div>
              </div>
            </section>
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>
              {t('translation:common.actions.cancel')}
            </button>
            <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
              {t('pages:salarySetting.save')}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('pages:salarySetting.deleteTitle')}
        message={t('pages:salarySetting.deleteMessage')}
        confirmText={t('translation:common.actions.delete')}
        cancelText={t('translation:common.actions.cancel')}
        type="danger"
        t={t}
      />
    </>
  );
};

export default SalarySettingDialog;
